import { useState } from 'react';
import { Link } from 'react-router-dom';
import { ChevronLeft, KeyRound, Mail } from 'lucide-react';
import { supabase } from '@/lib/supabase';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { AuthShell, AuthHeading } from '@/components/auth/AuthShell';
import { AuthAlert } from '@/components/auth/AuthAlert';
import { AuthStatus } from '@/components/auth/AuthStatus';
import { isNetworkError, NETWORK_ERROR_MESSAGE } from '@/services/network';
import { requestPasswordReset } from '@/services/coach-client';
import { appPath, appUrl } from '@/lib/basePath';

export default function ForgotPasswordPage() {
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  // Coach email-link accounts have no password to reset (auth_email_has_password).
  const [noPassword, setNoPassword] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    if (!email.trim()) { setError('Email is required.'); return; }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim())) { setError('Please enter a valid email address.'); return; }

    setLoading(true);
    try {
      // No password on this account (Coach email-link signup): nothing to
      // reset. Any RPC error falls through to the normal reset flow.
      const { data: hasPassword } = await supabase.rpc('auth_email_has_password', { p_email: email.trim() });
      if (hasPassword === false) { setNoPassword(true); return; }

      // Our email through the coach worker first. Supabase's own reset email
      // follows the project's shared Site URL (the brand dashboard's), so its
      // link can land on the wrong site; it stays only as the fallback.
      const viaWorker = await requestPasswordReset(email.trim());
      if (viaWorker.status === 'sent') { setSuccess(true); return; }
      if (viaWorker.status === 'cooldown') { setError(`We just sent you a reset link. You can request another in ${viaWorker.retryAfter} seconds.`); return; }
      if (viaWorker.status === 'error') { setError(viaWorker.message); return; }

      const redirectTo = typeof window !== 'undefined' ? appUrl('/reset-password') : appPath('/reset-password');
      const { error: resetError } = await supabase.auth.resetPasswordForEmail(email.trim(), { redirectTo });

      if (resetError) {
        setError(isNetworkError(resetError) ? NETWORK_ERROR_MESSAGE : resetError.message || 'Failed to send reset link. Please try again.');
        return;
      }
      setSuccess(true);
    } catch (err: unknown) {
      setError(isNetworkError(err) ? NETWORK_ERROR_MESSAGE : 'Something went wrong. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  if (noPassword) {
    return (
      <AuthShell>
        <AuthStatus
          tone="primary"
          icon={<KeyRound size={32} />}
          title="No password set yet"
          action={
            <Link to="/auth" className="font-medium text-primary hover:text-primary/80 transition-colors">
              Email me a sign-in link
            </Link>
          }
        >
          <span className="font-semibold text-white">{email}</span> signs in with an email link, so there's no
          password to reset. Sign in with a link, then set a password in <strong>Settings</strong> to use it here and
          in the BMC app.
        </AuthStatus>
      </AuthShell>
    );
  }

  if (success) {
    return (
      <AuthShell>
        <AuthStatus
          tone="success"
          icon={<Mail size={32} />}
          title="Check your email"
          action={
            <button
              onClick={() => { setSuccess(false); setEmail(''); }}
              className="font-medium text-primary hover:text-primary/80 transition-colors"
            >
              Send again
            </button>
          }
        >
          We sent a password reset link to{' '}
          <span className="font-semibold text-white">{email}</span>. Follow it to choose a new
          password.
        </AuthStatus>
      </AuthShell>
    );
  }

  return (
    <AuthShell>
      <Link
        to="/auth"
        className="mb-8 inline-flex items-center gap-1 text-[15px] text-white/55 hover:text-white/80 transition-colors"
      >
        <ChevronLeft size={20} />
        Back to sign in
      </Link>

      <AuthHeading title="Reset password" subtitle="Enter your email and we'll send you a reset link." />

      <form onSubmit={handleSubmit} className="mt-6 flex flex-col gap-4">
        <Input
          leftIcon={<Mail size={18} />}
          type="email"
          placeholder="Email address"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          autoComplete="email"
          autoCapitalize="none"
          inputMode="email"
        />

        {error && <AuthAlert variant="error">{error}</AuthAlert>}

        <Button type="submit" variant="primary" fullWidth loading={loading} className="mt-2">
          Send Reset Link
        </Button>
      </form>
    </AuthShell>
  );
}
